import React, { useEffect, useState } from 'react';
import { apiCall } from '../utils/api';
import { useIsMobile } from '../hooks/useIsMobile';

interface QueueItem {
  id: number;
  title: string;
  artist: string;
  user_name: string;
  status: string;
}

const NowNextPage: React.FC = () => {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchQueue = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiCall('/api/songs/queue');
        if (!res.ok) throw new Error('Failed to fetch queue');
        const data = await res.json();
        setQueue(data);
      } catch (err: any) {
        setError(err.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    };
    fetchQueue();
    // Refresh every 5 seconds to stay synchronized with Lyrics and Charts
    const interval = setInterval(fetchQueue, 5000);
    return () => clearInterval(interval);
  }, []);

  // Find the currently playing song first, then use queue order
  const playingSong = queue.find(item => item.status === 'playing');
  const queuedSongs = queue.filter(item => item.status === 'queued');

  const now = playingSong || queuedSongs[0] || queue[0];
  const next = playingSong ? queuedSongs[0] : queuedSongs[1] || queue[1];
  const afterNext = playingSong ? queuedSongs[1] : queuedSongs[2] || queue[2];

  const cardBase: React.CSSProperties = {
    flex: isMobile ? '1 1 100%' : '1 1 300px',
    padding: isMobile ? 20 : 32,
    background: 'rgba(40,25,15,0.95)',
    border: '1px solid #5a3a1a',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
    borderTop: 'none',
    position: 'relative',
    overflow: 'hidden',
  };

  const accentBar = (color: string): React.CSSProperties => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: color,
  });

  const songTitleStyle: React.CSSProperties = {
    fontSize: 'clamp(1.2rem, 3vw, 2.5rem)',
    fontWeight: 700,
    marginBottom: isMobile ? 8 : 16,
    color: '#f5deb3',
  };

  const artistStyle: React.CSSProperties = {
    fontSize: 'clamp(1rem, 2.5vw, 2rem)',
    color: '#a08060',
    fontStyle: 'italic',
    marginBottom: isMobile ? 8 : 16,
  };

  const userStyle: React.CSSProperties = {
    fontSize: 'clamp(1rem, 2.5vw, 2rem)',
    color: '#a08060',
  };

  const userNameStyle: React.CSSProperties = {
    fontWeight: 600,
    color: '#c8850f',
  };

  return (
    <div style={{ textAlign: 'center', padding: isMobile ? 16 : 32, background: 'transparent', minHeight: '100vh' }}>
      <h1 style={{
        fontSize: 'clamp(2rem, 5vw, 5rem)',
        marginBottom: isMobile ? 24 : 64,
        letterSpacing: 2,
        color: '#ffd700',
        textShadow: '0 0 20px rgba(255,215,0,0.4), 0 0 40px rgba(255,215,0,0.2)',
      }}>Now & Next</h1>
      {loading && <p style={{ fontSize: 'clamp(1.2rem, 3vw, 2.5rem)', color: '#f5deb3' }}>Loading...</p>}
      {error && <p style={{ color: '#ff6b6b', fontSize: 'clamp(1.2rem, 3vw, 2.5rem)' }}>{error}</p>}
      {!loading && !error && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: isMobile ? 20 : 96, flexWrap: 'wrap' }}>
          {now && (
            <div style={cardBase}>
              <div style={accentBar('#ffd700')} />
              <h2 style={{ color: '#ffd700', fontSize: 'clamp(1.5rem, 4vw, 3rem)', marginBottom: isMobile ? 12 : 24 }}>Now</h2>
              <div style={songTitleStyle}>{now.title}</div>
              <div style={artistStyle}>{now.artist}</div>
              <div style={userStyle}>sung by <span style={userNameStyle}>{now.user_name}</span></div>
            </div>
          )}
          {next && (
            <div style={cardBase}>
              <div style={accentBar('#c8850f')} />
              <h2 style={{ color: '#c8850f', fontSize: 'clamp(1.5rem, 4vw, 3rem)', marginBottom: isMobile ? 12 : 24 }}>Next</h2>
              <div style={songTitleStyle}>{next.title}</div>
              <div style={artistStyle}>{next.artist}</div>
              <div style={userStyle}>sung by <span style={userNameStyle}>{next.user_name}</span></div>
            </div>
          )}
          {afterNext && (
            <div style={cardBase}>
              <div style={accentBar('#a67c52')} />
              <h2 style={{ color: '#a67c52', fontSize: 'clamp(1.5rem, 4vw, 3rem)', marginBottom: isMobile ? 12 : 24 }}>After Next</h2>
              <div style={songTitleStyle}>{afterNext.title}</div>
              <div style={artistStyle}>{afterNext.artist}</div>
              <div style={userStyle}>sung by <span style={userNameStyle}>{afterNext.user_name}</span></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NowNextPage;
